import { notifyError } from "@/lib/notify-error";
import { compareViewerSources } from "@/features/document-viewport/preparing-documents";
import { useMemo, useEffect, useRef, useState } from "react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { MeasurementType } from "@/lib/geometry";
import type {
  TagRecord,
  CountRow,
  ViewerSource,
  TakeoffViewerTag,
  MeasurementBreakdownTargetRow,
} from "@/types/takeoff-pilot";

export function useJobStatus(job: { status: string } | null | undefined) {
  return useMemo(() => ({
    isExtracting: job?.status === "extracting_schedule",
    isCopyingRevision: job?.status === "copying_revision",
    isSelecting: job?.status === "ready_for_tag_selection",
    isIndexingFinishPlan: job?.status === "indexing_finish_plan",
    isResolvingCounts: job?.status === "resolving_tag_counts",
    isCounting: job?.status === "indexing_finish_plan" || job?.status === "resolving_tag_counts",
    isResults: job?.status === "ready_for_results" || job?.status === "completed",
    showWorkspace: job?.status === "ready_for_tag_selection" ||
      job?.status === "indexing_finish_plan" ||
      job?.status === "resolving_tag_counts" ||
      job?.status === "ready_for_results" ||
      job?.status === "completed",
  }), [job?.status]);
}

function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export function useComputedValues(
  tags: TagRecord[],
  countRows: CountRow[] | undefined,
  measurementTotals:
    | {
        tagId: string;
        sqft: number;
        lf: number;
        cf: number;
        breakdown?: Array<{
          measurementType: MeasurementType;
          value: number;
          count: number;
        }>;
        breakdownTargets?: MeasurementBreakdownTargetRow[];
      }[]
    | undefined,
  viewerSources: ViewerSource[] | undefined
) {
  const includedTagIds = useMemo(
    () => new Set(tags.filter((tag) => tag.included).map((tag) => String(tag._id))),
    [tags]
  );

  const mergedCountRows = useMemo(() => {
    if (!countRows) return undefined;
    const totalsByTagId = new Map(
      (measurementTotals ?? [])
        .filter((m) => includedTagIds.has(String(m.tagId)))
        .map((m) => [
          String(m.tagId),
          {
            lf: m.lf,
            sqft: m.sqft,
            cf: m.cf,
            breakdown: m.breakdown ?? [],
            breakdownTargets: m.breakdownTargets ?? [],
          },
        ])
    );
    const rowsByTagId = new Map(countRows.map((row) => [String(row.tagId), row]));
    const mergedRows = countRows.map((row) => ({
      ...row,
      lf: totalsByTagId.get(String(row.tagId))?.lf ?? 0,
      sqft: totalsByTagId.get(String(row.tagId))?.sqft ?? 0,
      cf: totalsByTagId.get(String(row.tagId))?.cf ?? 0,
      breakdown: totalsByTagId.get(String(row.tagId))?.breakdown ?? row.breakdown ?? [],
      breakdownTargets:
        totalsByTagId.get(String(row.tagId))?.breakdownTargets ?? row.breakdownTargets ?? [],
    }));

    for (const [tagId, totals] of totalsByTagId.entries()) {
      if (rowsByTagId.has(tagId)) {
        continue;
      }
      const tag = tags.find((candidate) => String(candidate._id) === tagId);
      if (!tag) {
        continue;
      }
      mergedRows.push({
        tagId: tag._id,
        rawValue: tag.rawValue,
        colorHex: tag.colorHex,
        included: tag.included,
        count: 0,
        excludedCount: 0,
        lf: totals.lf,
        sqft: totals.sqft,
        cf: totals.cf,
        breakdown: totals.breakdown,
        breakdownTargets: totals.breakdownTargets,
      });
    }

    return mergedRows;
  }, [countRows, includedTagIds, measurementTotals, tags]);

  const selectedCount = useMemo(
    () => tags.filter((tag) => tag.included).length,
    [tags]
  );

  const selectedTagSignature = useMemo(
    () => tags.filter((tag) => tag.included).map((tag) => String(tag._id)).sort().join(","),
    [tags]
  );

  const selectedResults = useMemo(
    () => (mergedCountRows ?? [])
      .filter((row) => row.included)
      .sort(
        (a, b) =>
          b.count - a.count || b.sqft - a.sqft || a.rawValue.localeCompare(b.rawValue)
      ),
    [mergedCountRows]
  );

  const viewerTags = useMemo<TakeoffViewerTag[]>(() => {
    const tagById = new Map(tags.map((t) => [t._id, t]));
    return selectedResults
      .map((row) => {
        const tag = tagById.get(row.tagId);
        if (!tag) return null;
        return {
          tagId: row.tagId,
          isCustom:tag.isCustom,
          rawValue: row.rawValue,
          normalizedValue: tag.normalizedValue,
          ...(row.colorHex ? { colorHex: row.colorHex } : {}),
        };
      })
      .filter(isDefined);
  }, [selectedResults, tags]);

  const selectedTagsForExport = useMemo(
    () => tags.filter((tag) => tag.included),
    [tags]
  );

  const orderedViewerSources = useMemo(
    () => ((viewerSources ?? []))
      .filter((source) => Boolean(source.fileUrl))
      .sort(compareViewerSources),
    [viewerSources]
  );

  return {
    includedTagIds,
    mergedCountRows,
    selectedCount,
    selectedTagSignature,
    selectedResults,
    viewerTags,
    selectedTagsForExport,
    orderedViewerSources,
  };
}

export function useResolvedViewerSources({
  jobId,
  viewerSources,
  orderedViewerSources,
  selectedPlanDocumentId,
  setSelectedPlanDocumentId,
}: {
  jobId: Id<"takeoffPilotJobs"> | undefined;
  viewerSources: ViewerSource[] | undefined;
  orderedViewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  setSelectedPlanDocumentId: (value: string) => void;
}) {
  const [stableViewerSourceState, setStableViewerSourceState] = useState<{
    jobId: string;
    viewerSources: ViewerSource[];
  }>({
    jobId: String(jobId ?? ""),
    viewerSources: [],
  });

  useEffect(() => {
    if (viewerSources === undefined) {
      return;
    }

    setStableViewerSourceState({
      jobId: String(jobId ?? ""),
      viewerSources: orderedViewerSources,
    });
  }, [jobId, orderedViewerSources, viewerSources]);

  const resolvedViewerSources =
    viewerSources === undefined
      ? stableViewerSourceState.jobId === String(jobId ?? "")
        ? stableViewerSourceState.viewerSources
        : []
      : orderedViewerSources;

  // Only a user's choice is stored; an empty selection opens the default source
  // (see `defaultViewerSource`). A choice still being prepared is kept; one that
  // leaves the job falls back to the default.
  useEffect(() => {
    if (
      viewerSources !== undefined &&
      selectedPlanDocumentId &&
      !viewerSources.some((source) => source.sourceId === selectedPlanDocumentId)
    ) {
      setSelectedPlanDocumentId("");
    }
  }, [selectedPlanDocumentId, setSelectedPlanDocumentId, viewerSources]);

  return resolvedViewerSources;
}

export function useTakeoffSelectionRecount({
  detailsLoaded,
  showWorkspace,
  selectedTagSignature,
  isCounting,
  isSelecting,
  jobId,
  queuePlanAnalysis,
}: {
  detailsLoaded: boolean;
  showWorkspace: boolean;
  selectedTagSignature: string;
  isCounting: boolean;
  isSelecting: boolean;
  jobId: Id<"takeoffPilotJobs"> | undefined;
  queuePlanAnalysis: (args: { jobId: Id<"takeoffPilotJobs"> }) => Promise<unknown>;
}) {
  const previousSelectedTagSignatureRef = useRef<string | null>(null);
  const recountTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!jobId || !detailsLoaded || !showWorkspace) {
      return;
    }

    if (!isSelecting) {
      return;
    }

    if (previousSelectedTagSignatureRef.current === null) {
      previousSelectedTagSignatureRef.current = selectedTagSignature;
      return;
    }

    if (previousSelectedTagSignatureRef.current === selectedTagSignature) {
      return;
    }

    previousSelectedTagSignatureRef.current = selectedTagSignature;

    if (recountTimeoutRef.current) {
      clearTimeout(recountTimeoutRef.current);
      recountTimeoutRef.current = null;
    }

    if (selectedTagSignature.length === 0 || isCounting) {
      return;
    }

    recountTimeoutRef.current = setTimeout(() => {
      void queuePlanAnalysis({ jobId }).catch((error) => {
        notifyError(error, "Failed to update counting results");
        console.error(error);
      });
    }, 500);

    return () => {
      if (recountTimeoutRef.current) {
        clearTimeout(recountTimeoutRef.current);
        recountTimeoutRef.current = null;
      }
    };
  }, [detailsLoaded, isCounting, isSelecting, jobId, queuePlanAnalysis, selectedTagSignature, showWorkspace]);
}
